import { ChangeDetectorRef, Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatToolbar, MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, iif, of } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { SidenavContentService } from './sidenav-content.service';
import { SidenavContentContainerDirective } from './sidenav-content.directive';
import { ToolbarTitleDirective } from '../../directives/toolbar-title.directive';
import { FlexSpacerDirective } from '../../directives/flex-spacer.directive';
import { SidenavContentModule } from './sidenav-content.module';

@UntilDestroy()
@Component({
    selector: 'app-sidenav-content',
    styleUrls: ['./sidenav-content.component.scss'],
    templateUrl: './sidenav-content.component.html',
    standalone: true,
    host: { class: 'app-sidenav-content' },
    imports: [
        MatToolbarModule,
        ToolbarTitleDirective,
        FlexSpacerDirective,
        MatIconModule,
        MatButtonModule,
        MatProgressBarModule,
        SidenavContentContainerDirective,
        AsyncPipe,
        SidenavContentModule,
    ],
})
export class SidenavContentComponent implements OnInit
{
    title = '';
    showActions = false;

    readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _headerToolbar: ElementRef;
    private _actionsToolbar: ElementRef;

    // temp store for mat-menus, buttons, or toolbar elements projected into the header toolbar on init
    private _headerToolbarExtras = [];

    constructor(
        private _sidenav: SidenavContentService,
        private _renderer: Renderer2,
        private _cd: ChangeDetectorRef,
    ) {
    }

    @ViewChild(SidenavContentContainerDirective, { static: true }) set content(content: SidenavContentContainerDirective) {
        if (content) {
            this._sidenav._container = content.viewContainerRef;
        }
    }

    @ViewChild('sidenavHeader', { static: true }) set header(content: MatToolbar) {
        if (content) {
            this._headerToolbar = content._elementRef;
            this._renderer.addClass(this._headerToolbar.nativeElement, 'sidenav-content-header');
            this._renderer.addClass(this._headerToolbar.nativeElement, 'animated');
            this._renderer.addClass(this._headerToolbar.nativeElement, 'fadeIn');
        }
    }

    @ViewChild('sidenavActions', { static: true }) set actions(content: MatToolbar) {
        if (content) {
            this._actionsToolbar = content._elementRef;
            this._renderer.addClass(this._actionsToolbar.nativeElement, 'sidenav-content-actions');
            this._renderer.addClass(this._actionsToolbar.nativeElement, 'animated');
            this._renderer.addClass(this._actionsToolbar.nativeElement, 'fadeIn');
        }
    }

    ngOnInit() {
        this._sidenav._loading$.pipe(switchMap((loading) => iif(() => loading, of(loading), of(loading).pipe(delay(900)))), untilDestroyed(this)).subscribe({
            next: (loading) => this.loading$.next(loading),
        });

        /** When the title subject pushes a new value, update the title text displayed in the sidenav header */
        this._sidenav._title$.pipe(untilDestroyed(this)).subscribe((elementRef) => {
            if (this._headerToolbarExtras.length > 0) {
                // clean up mat-menus, buttons, or toolbar elements added for sidenav content
                // checks on sidenav open/close just in case
                for (const node of this._headerToolbarExtras) {
                    this._renderer.removeChild(this._headerToolbar.nativeElement, node);
                }
                this._headerToolbarExtras = [];
            }

            if (elementRef) {
                const nodes = elementRef.nativeElement.childNodes;
                if (nodes && nodes.length > 0) {
                    this.title = nodes[0].textContent;
                    const spacer = this._headerToolbar.nativeElement.querySelector('toolbar-spacer');
                    for (const nodeIndex in nodes) {
                        if (nodes[nodeIndex] && +nodeIndex > 0) {
                            this._headerToolbarExtras.push(nodes[nodeIndex]);
                            this._renderer.insertBefore(this._headerToolbar.nativeElement, nodes[nodeIndex], spacer);
                        }
                    }

                    this._renderer.removeChild(elementRef.nativeElement.parent, elementRef.nativeElement);
                    this._cd.detectChanges();
                }
            } else {
                this.title = '';
            }
        });

        /**
         * When the actions subject pushes a new value, we determine if actions are included
         * in the sidenav (or actions are removed when closing the sidenav)
         */
        this._sidenav._actions$.pipe(untilDestroyed(this)).subscribe((elementRef) => {
            const sidenavActionToolbar = this._actionsToolbar.nativeElement;
            if (elementRef) {
                // move each child node (like mat-buttons) to the actions toolbar
                while (elementRef.nativeElement.firstChild) {
                    this._renderer.appendChild(sidenavActionToolbar, elementRef.nativeElement.firstChild);
                }
                this.showActions = true;
            } else {
                // remove child nodes and resets the actions toolbar
                while (sidenavActionToolbar.firstChild) {
                    sidenavActionToolbar.removeChild(sidenavActionToolbar.lastChild);
                }
                this.showActions = false;
            }
            this._cd.detectChanges();
        });
    }
}
